<template>
  <div class="flex flex-col gap-14 overflow-scroll h-screen p-8">
    <div
      v-for="{ customer, tenants } in tenantsByCustomer"
      :key="customer.id"
      class="flex flex-col gap-4 bg-gray-50 p-4 rounded-xl border"
    >
      <h1 class="text-xl font-medium">{{ customer.company_name }}</h1>
      <ul
        role="list"
        class="grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-3 xl:gap-x-8"
      >
        <li
          v-for="tenant in tenants"
          :key="tenant.id"
          class="overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
          <div class="flex items-center gap-x-4 border-b border-gray-900/5 p-6">
            <div
              class="h-12 w-12 flex shrink-0 items-center justify-center rounded-md bg-white object-cover ring-1 ring-gray-900/10"
            >
              <building-seven class="text-gray-900" />
            </div>
            <div class="flex grow justify-between overflow-hidden">
              <div
                class="flex flex-col text-sm font-medium leading-6 text-gray-900 overflow-hidden pr-2"
              >
                <span class="truncate">{{ tenant.name }}</span>
              </div>
              <div class="flex shrink-0">
                <span
                  v-if="tenant.isSubscribed"
                  class="inline-flex items-center rounded-sm bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                >
                  Stripe Subscription
                </span>
                <span
                  v-else-if="tenant.hasCustomSubscription"
                  class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                >
                  Custom Subscription
                </span>
                <span
                  v-else-if="tenant.hasTrial"
                  class="inline-flex items-center rounded-sm bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20"
                >
                  Active Trial
                </span>
                <span
                  v-else
                  class="inline-flex items-center rounded-sm bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10"
                >
                  Expired Trial
                </span>
              </div>
            </div>
          </div>
          <div class="px-4 pt-4">
            <select
              v-if="props.isSuperAdmin"
              :disabled="isAssigningInProgress"
              @change="handleAssignCustomer(tenant.id, $event)"
              class="w-full overflow-hidden text-ellipsis disabled:opacity-50 appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            >
              <option
                v-for="customer in customers"
                :key="customer.id"
                :value="customer.id"
                :selected="tenant.customer.id === customer.id"
              >
                {{ customer.company_name }} - ({{ customer.id }})
              </option>
            </select>
            <span v-else class="text-xs text-gray-600">
              {{ tenant.customer.company_name }}
            </span>
          </div>
          <dl
            class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm leading-6"
          >
            <div
              v-if="props.isSuperAdmin"
              class="flex justify-between gap-x-4 py-3"
            >
              <dt class="text-gray-500 font-medium">Custom Subscription</dt>
              <Switch
                :model-value="tenant.hasCustomSubscription"
                @update:model-value="
                  (value) => toggleSubscription(tenant.id, value)
                "
                :class="[
                  tenant.hasCustomSubscription ? 'bg-blue-600' : 'bg-gray-200',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2',
                ]"
              >
                <span class="sr-only">Toggle Subscription</span>
                <span
                  aria-hidden="true"
                  :class="[
                    tenant.hasCustomSubscription
                      ? 'translate-x-5'
                      : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  ]"
                />
              </Switch>
            </div>
            <div class="flex justify-between gap-x-4 py-3">
              <dt class="text-gray-500 font-medium">Spaces</dt>
              <dd class="text-gray-700 font-medium">{{ tenant.spaces }}</dd>
            </div>
            <div class="flex justify-between gap-x-4 py-3">
              <dt class="text-gray-500 font-medium">Assets</dt>
              <dd class="text-gray-700 font-medium">{{ tenant.assets }}</dd>
            </div>
            <div class="flex justify-between gap-x-4 py-3">
              <dt class="text-gray-500 font-medium">Asset Groups</dt>
              <dd class="text-gray-700 font-medium">
                {{ tenant.assetGroups }}
              </dd>
            </div>
            <div class="flex justify-between gap-x-4 py-3">
              <dt class="text-gray-500 font-medium">Documents</dt>
              <dd class="text-gray-700 font-medium">
                {{ tenant.documents }}
              </dd>
            </div>
            <div class="flex flex-col gap-4">
              <ul role="list" class="divide-y divide-gray-100">
                <li
                  @click="impersonate(user)"
                  v-for="user in tenant.users"
                  :key="user.id"
                  class="flex items-center justify-between gap-x-4 py-5 group"
                  :class="{
                    'hover:cursor-pointer': !user.admin,
                    'hover:cursor-not-allowed': user.admin,
                  }"
                >
                  <div class="min-w-0">
                    <p
                      class="text-sm font-semibold leading-6"
                      :class="{
                        'text-gray-900': !user.admin,
                        'text-gray-400': user.admin,
                      }"
                    >
                      {{ user.name }}
                    </p>
                    <p
                      class="mt-1 truncate font-medium text-xs leading-5"
                      :class="{
                        'text-gray-500': !user.admin,
                        'text-gray-400': user.admin,
                      }"
                    >
                      {{ user.email }}
                    </p>
                  </div>
                  <div>
                    <ChevronRight
                      class="text-gray-400"
                      :class="{ 'group-hover:text-gray-900': !user.admin }"
                    />
                  </div>
                </li>
              </ul>
            </div>
          </dl>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import BuildingSeven from "@/Tailwind/Icon/BuildingSeven.vue";
import ChevronRight from "@/Tailwind/Icon/ChevronRight.vue";
import { Switch } from "@headlessui/vue";
import { computed, ref } from "vue";

const props = defineProps({
  tenants: Array,
  isSuperAdmin: Boolean,
  customers: Array,
});

const isAssigningInProgress = ref(false);

const tenantsByCustomer = computed(() => {
  const groupedTenants = props.tenants.reduce((acc, tenant) => {
    const customerId = tenant.customer.id;

    if (!acc[customerId]) {
      acc[customerId] = {
        customer: tenant.customer,
        tenants: [],
      };
    }

    acc[customerId].tenants.push(tenant);

    return acc;
  }, {});

  return Object.fromEntries(
    Object.entries(groupedTenants).sort((a, b) =>
      a[1].customer.company_name.localeCompare(b[1].customer.company_name)
    )
  );
});

const impersonate = (user) => {
  if (user.admin) {
    return;
  }

  router.post(route("impersonate.start"), { id: user.id });
};

const toggleSubscription = (tenantId, subscribed) => {
  router.post(route("impersonate.subscribe"), {
    tenant_id: tenantId,
    subscribed,
  });
};

const handleAssignCustomer = (tenantId, event) => {
  router.post(
    route("impersonate.customer.assign"),
    {
      tenant_id: tenantId,
      customer_id: event.target.value,
    },
    {
      onStart: () => {
        isAssigningInProgress.value = true;
      },
      onSuccess: () => {
        isAssigningInProgress.value = false;
      },
    }
  );
};
</script>
